/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Service for operating on the current user's favorite (pinned) connections
 * via the REST API.
 */
angular.module('rest').factory('favoriteConnectionService', ['$injector',
        function favoriteConnectionService($injector) {

    // Required services
    var authenticationService = $injector.get('authenticationService');
    var cacheService           = $injector.get('cacheService');

    // Required types
    var RelatedObjectPatch = $injector.get('RelatedObjectPatch');

    var service = {};

    /**
     * Creates a new array of patches which represents the given changes to
     * the current user's set of favorite connections.
     *
     * @param {String[]} [identifiersToAdd]
     *     The identifiers of all connections which should be added to the
     *     set of favorites, if any.
     *
     * @param {String[]} [identifiersToRemove]
     *     The identifiers of all connections which should be removed from
     *     the set of favorites, if any.
     *
     * @returns {RelatedObjectPatch[]}
     *     A new array of patches which represents the given changes.
     */
    var getRelatedObjectPatch = function getRelatedObjectPatch(identifiersToAdd, identifiersToRemove) {

        var patch = [];

        angular.forEach(identifiersToAdd, function addIdentifier(identifier) {
            patch.push(new RelatedObjectPatch({
                op    : RelatedObjectPatch.Operation.ADD,
                value : identifier
            }));
        });

        angular.forEach(identifiersToRemove, function removeIdentifier(identifier) {
            patch.push(new RelatedObjectPatch({
                op    : RelatedObjectPatch.Operation.REMOVE,
                value : identifier
            }));
        });

        return patch;

    };

    /**
     * Returns the URL for the REST resource representing the current user's
     * favorite connections within the given data source.
     *
     * @param {String} dataSource
     *     The unique identifier of the data source to retrieve the URL for.
     *     This identifier corresponds to an AuthenticationProvider within the
     *     Guacamole web application.
     *
     * @returns {String}
     *     The URL for the REST resource representing the current user's
     *     favorite connections within the given data source.
     */
    var getFavoriteConnectionsResourceURL = function getFavoriteConnectionsResourceURL(dataSource) {
        return 'api/session/data/' + encodeURIComponent(dataSource) + '/self/favoriteConnections';
    };

    /**
     * Makes a request to the REST API to retrieve the identifiers of all
     * connections that the current user has marked as a favorite within the
     * given data source, returning a promise that can be used for processing
     * the results of the call.
     *
     * @param {String} dataSource
     *     The unique identifier of the data source to retrieve favorite
     *     connections from. This identifier corresponds to an
     *     AuthenticationProvider within the Guacamole web application.
     *
     * @returns {Promise.<String[]>}
     *     A promise for the HTTP call which will resolve with an array
     *     containing the identifiers of all favorite connections within the
     *     given data source upon success.
     */
    service.getFavoriteConnections = function getFavoriteConnections(dataSource) {

        return authenticationService.request({
            cache   : cacheService.connections,
            method  : 'GET',
            url     : getFavoriteConnectionsResourceURL(dataSource)
        });

    };

    /**
     * Makes a request to the REST API to modify the set of connections that
     * the current user has marked as a favorite within the given data
     * source, returning a promise that can be used for processing the
     * results of the call.
     *
     * @param {String} dataSource
     *     The unique identifier of the data source containing the
     *     connections being favorited/unfavorited. This identifier
     *     corresponds to an AuthenticationProvider within the Guacamole web
     *     application.
     *
     * @param {String[]} [identifiersToAdd]
     *     The identifiers of all connections which should be added to the
     *     set of favorites, if any.
     *
     * @param {String[]} [identifiersToRemove]
     *     The identifiers of all connections which should be removed from
     *     the set of favorites, if any.
     *
     * @returns {Promise}
     *     A promise for the HTTP call which will succeed if and only if the
     *     patch operation is successful.
     */
    service.patchFavoriteConnections = function patchFavoriteConnections(dataSource,
            identifiersToAdd, identifiersToRemove) {

        return authenticationService.request({
            method  : 'PATCH',
            url     : getFavoriteConnectionsResourceURL(dataSource),
            data    : getRelatedObjectPatch(identifiersToAdd, identifiersToRemove)
        })

        // Clear the cache
        .then(function favoriteConnectionsChanged() {
            cacheService.connections.removeAll();
        });

    };

    return service;

}]);
